import { useCallback, useEffect, useMemo, useState } from 'react';
import { TableV3 } from 'common/table';
import { TableV3Filters } from 'common/table/components/TableV3';
import { TableV3Sort } from 'common/table/types/TableV3';
import { applicantsTableColumns } from '../tables/columns/applicantsTableColumns';
import { mapApplicantsDataToTableColumns } from '../tables/maps/mapApplicantsDataToTableColumns';
import { ApplicantsTableColumns } from '../tables/types/ApplicantsTable';
import { useSearchParams } from 'react-router-dom';
import { useDrawer } from 'services/drawer/hooks';
import { DrawerComponentType } from 'services/drawer/types';
import {
  deleteSearchParams,
  updateSearchParams
} from 'services/navigation/paths';
import { useSearchCandidatesWithSubscription } from 'pages/CandidatesRanking/hooks/useSearchCandidatesWithSubscription';
import {
  SearchableCandidateSortableFields,
  SearchableCandidateSortInput,
  SearchableSortDirection
} from 'generated-graphql/types';
import { getApplicantsFilters } from '../utils/getApplicantsFilters';
import { useRecoilValue } from 'recoil';
import { integrationAtom } from 'services/integration/atom';
import { ApolloError } from '@apollo/client';

const DEFAULT_PAGE_SIZE = 50;

type ApplicantsTableProps = {
  organizationId: string;
  onSubscriptionError?: (error: ApolloError) => void;
};

export const ApplicantsTable: React.FC<ApplicantsTableProps> = ({
  organizationId,
  onSubscriptionError
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { openDrawer } = useDrawer();

  const integration = useRecoilValue(integrationAtom);

  const [filters, setFilters] = useState<
    TableV3Filters<ApplicantsTableColumns>
  >({});
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [from, setFrom] = useState(0);
  const [sort, setSort] = useState<
    TableV3Sort<SearchableCandidateSortableFields>
  >({
    field: SearchableCandidateSortableFields.CreatedAt,
    direction: 'desc'
  });

  const currentPage = from / pageSize;

  const handleChangePage = (page: number) => {
    setFrom(page * pageSize);
  };

  const handleChangePageSize = (newPageSize: number) => {
    setPageSize(newPageSize);
    setFrom(0);
  };

  const queryFilters = getApplicantsFilters({
    organizationId,
    filters
  });

  const sortInput: SearchableCandidateSortInput = {
    field: sort.field,
    direction: sort.direction as SearchableSortDirection
  };

  const {
    candidates: candidatesList,
    total: totalCandidates,
    loading,
    subscriptionError
  } = useSearchCandidatesWithSubscription({
    filter: queryFilters,
    limit: pageSize,
    from,
    sort: [sortInput]
  });

  useEffect(() => {
    if (totalCandidates > 0) {
      setTotal(totalCandidates);
    }
  }, [totalCandidates]);

  useEffect(() => {
    if (subscriptionError) {
      onSubscriptionError?.(subscriptionError);
    }
  }, [subscriptionError, onSubscriptionError]);

  const handleViewApplicant = useCallback(
    (row: ApplicantsTableColumns | undefined) => {
      if (!row) return;

      const candidate = candidatesList.find((c) => c.id === row.id);
      if (!candidate) return;

      updateSearchParams({
        searchParams,
        newParam: {
          key: 'candidateId',
          value: candidate.id
        },
        setSearchParams
      });

      openDrawer({
        drawerContentType: DrawerComponentType.CandidateReport,
        drawerComponentProps: {
          candidateId: candidate.id,
          candidateTableIndex: null,
          candidateIds: null
        },
        onClose: () => {
          deleteSearchParams({
            searchParams,
            key: 'candidateId',
            setSearchParams
          });
        }
      });
    },
    [candidatesList, searchParams, setSearchParams, openDrawer]
  );

  const mappedApplicants = useMemo(
    () => mapApplicantsDataToTableColumns(candidatesList || []),
    [candidatesList]
  );

  const columns = useMemo(
    () =>
      applicantsTableColumns(handleViewApplicant, !!integration, integration),
    [handleViewApplicant, integration]
  );

  return (
    <TableV3
      tableId="applicants-table"
      columns={columns}
      data={mappedApplicants}
      onFiltersChanged={(newFilters) => {
        setFilters(newFilters);
        setFrom(0);
      }}
      defaultSort={sort}
      onSort={setSort}
      loading={loading}
      paginationOptions={{
        pageSize,
        totalRows: total,
        currentPage,
        onChangePage: handleChangePage,
        onChangePageSize: handleChangePageSize
      }}
    />
  );
};
